import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DeviceCreate } from '../../api/models';
import { useI18n } from '../../contexts/I18nContext';
import { deviceService, DeviceSearchHit } from '../../services/deviceService';
import { safeStorage } from '../../utils/safeStorage';
import { DeviceDetailModal } from '../devices/DeviceDetailModal';
import { SearchIcon, CloseIcon } from './icons';
import './GlobalSearch.css';

export type SearchMode = 'imei' | 'phone';
type Status = 'idle' | 'searching' | 'invalid' | 'notFound' | 'forbidden' | 'error';

const IMEI_RE = /^[0-9A-Za-z]{15}$/;
const PHONE_RE = /^[0-9]{8,15}$/;
const MIN_SUGGEST_CHARS = 3;
const SUGGEST_DELAY_MS = 250;
const RECENT_KEY = 'gsearch.recent';
const RECENT_MAX = 6;

/** Digits only; a leading "+" or "00" is dropped so 33… is what the API gets. */
export const normalisePhone = (raw: string): string => {
  const digits = raw.replace(/[\s.\-()]/g, '');
  if (digits.startsWith('+')) return digits.slice(1);
  if (digits.startsWith('00')) return digits.slice(2);
  return digits;
};

/** What the typed value looks like, when it is unambiguous; null otherwise. */
export const detectMode = (raw: string): SearchMode | null => {
  const value = raw.trim();
  if (IMEI_RE.test(value) && /[A-Za-z]/.test(value)) return 'imei';
  if (/^[0-9]{15}$/.test(value)) return 'imei';
  if (/^(\+|00)[0-9\s.\-()]{6,}$/.test(value)) return 'phone';
  return null;
};

const statusOf = (err: unknown): Status => {
  const code = (err as { response?: { status?: number } })?.response?.status;
  if (code === 404) return 'notFound';
  if (code === 403) return 'forbidden';
  return 'error';
};

const readRecent = (): DeviceSearchHit[] => {
  try {
    const parsed = JSON.parse(safeStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((r) => r && typeof r.idDevice === 'string') : [];
  } catch {
    return [];
  }
};

const writeRecent = (hits: DeviceSearchHit[]) => {
  try {
    safeStorage.setItem(RECENT_KEY, JSON.stringify(hits.slice(0, RECENT_MAX)));
  } catch {
    /* storage unavailable: recent searches are a convenience only */
  }
};

const toDevice = (hit: DeviceSearchHit): DeviceCreate =>
  ({
    idDevice: hit.idDevice,
    idClient: hit.idClient ?? undefined,
    deviceType: hit.deviceType,
    phone: hit.phone,
    needsRefresh: hit.needsRefresh,
  } as unknown as DeviceCreate);

/**
 * The search bar in the top bar: an IMEI or a phone number, with
 * suggestions as you type, the last devices opened, and the device dialog
 * right here, whatever page is underneath.
 */
export const GlobalSearch: React.FC = () => {
  const { t } = useI18n();
  const [mode, setMode] = useState<SearchMode>('imei');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [device, setDevice] = useState<DeviceCreate | null>(null);
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<DeviceSearchHit[]>([]);
  const [recent, setRecent] = useState<DeviceSearchHit[]>(() => readRecent());
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLFormElement>(null);
  const requestSeq = useRef(0);

  // Ctrl+K / Cmd+K brings the cursor to the bar from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A click anywhere else closes the list.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Suggestions follow the typed text, after a short pause and only from a
  // few characters on. A stale answer (slower request) is dropped.
  useEffect(() => {
    const raw = query.trim();
    if (raw.length < MIN_SUGGEST_CHARS) {
      setHits([]);
      setHighlight(-1);
      return;
    }
    const seq = ++requestSeq.current;
    const timer = window.setTimeout(async () => {
      try {
        const found = await deviceService.searchDevices(mode === 'phone' ? normalisePhone(raw) : raw, 8);
        if (seq !== requestSeq.current) return;
        setHits(found);
        setHighlight(found.length > 0 ? 0 : -1);
      } catch {
        if (seq === requestSeq.current) setHits([]);
      }
    }, SUGGEST_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [query, mode]);

  const switchMode = (next: SearchMode) => {
    setMode(next);
    setStatus('idle');
    inputRef.current?.focus();
  };

  const clear = () => {
    setQuery('');
    setHits([]);
    setHighlight(-1);
    setStatus('idle');
    setOpen(false);
    inputRef.current?.focus();
  };

  const openDevice = useCallback((hit: DeviceSearchHit) => {
    const next = [hit, ...recent.filter((r) => r.idDevice !== hit.idDevice)].slice(0, RECENT_MAX);
    setRecent(next);
    writeRecent(next);
    setDevice(toDevice(hit));
    setOpen(false);
    setStatus('idle');
  }, [recent]);

  const forgetRecent = () => {
    setRecent([]);
    writeRecent([]);
  };

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (open && highlight >= 0 && hits[highlight]) {
      openDevice(hits[highlight]);
      return;
    }
    const raw = query.trim();
    if (!raw || status === 'searching') return;
    const value = mode === 'phone' ? normalisePhone(raw) : raw.toUpperCase();
    if ((mode === 'imei' && !IMEI_RE.test(value)) || (mode === 'phone' && !PHONE_RE.test(value))) {
      setStatus('invalid');
      return;
    }
    setStatus('searching');
    try {
      const deviceId = mode === 'phone' ? await deviceService.findDeviceIdByPhone(value) : value;
      if (!deviceId) throw Object.assign(new Error('not found'), { response: { status: 404 } });
      const detail = await deviceService.getDeviceDetail(deviceId);
      openDevice({
        idDevice: detail.idDevice,
        idClient: detail.idClient ?? null,
        deviceType: detail.deviceType,
        phone: detail.phone,
        needsRefresh: detail.needsRefresh,
      });
    } catch (err) {
      setStatus(statusOf(err));
    }
  };

  const onChange = (value: string) => {
    setQuery(value);
    setOpen(true);
    if (status !== 'idle' && status !== 'searching') setStatus('idle');
    // "+33…" is a phone number, fifteen characters an IMEI: follow the text
    // so the mode buttons are only needed for the ambiguous cases.
    const detected = detectMode(value);
    if (detected && detected !== mode) setMode(detected);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const list = query.trim().length >= MIN_SUGGEST_CHARS ? hits : recent;
    if (e.key === 'Escape') {
      if (open) setOpen(false);
      else clear();
      return;
    }
    if (!list.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h + 1) % list.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h <= 0 ? list.length - 1 : h - 1));
    } else if (e.key === 'Enter' && open && highlight >= 0 && list === recent) {
      e.preventDefault();
      openDevice(recent[highlight]);
    }
  };

  const message: Record<Exclude<Status, 'idle' | 'searching'>, string> = {
    invalid: mode === 'imei' ? t('globalSearch.invalidImei') : t('globalSearch.invalidPhone'),
    notFound: t('globalSearch.notFound'),
    forbidden: t('globalSearch.forbidden'),
    error: t('globalSearch.error'),
  };

  const typing = query.trim().length >= MIN_SUGGEST_CHARS;
  const showList = open && (typing ? hits.length > 0 : recent.length > 0);
  const listed = typing ? hits : recent;

  const renderHit = (hit: DeviceSearchHit, index: number) => (
    <li
      key={hit.idDevice}
      role="option"
      aria-selected={index === highlight}
      className={`gsearch-hit${index === highlight ? ' is-highlighted' : ''}`}
      onMouseEnter={() => setHighlight(index)}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => openDevice(hit)}
      data-testid="gsearch-hit"
    >
      <span className="gsearch-hit-id">{hit.idDevice}</span>
      <span className="gsearch-hit-type">{hit.deviceType || '—'}</span>
      <span className="gsearch-hit-phone">{hit.phone && hit.phone !== '000000000000000' ? hit.phone : '—'}</span>
      {hit.needsRefresh && <span className="gsearch-hit-dot" title={t('devices.needsRefresh')} />}
    </li>
  );

  return (
    <>
      <form
        ref={rootRef}
        className={`gsearch${status === 'searching' ? ' searching' : ''}`}
        role="search"
        aria-label={t('globalSearch.label')}
        onSubmit={submit}
      >
        <div className="gsearch-pill">
          <button type="submit" className="gsearch-icon-btn" aria-label={t('globalSearch.submit')} disabled={status === 'searching'} data-testid="gsearch-submit">
            <SearchIcon />
          </button>
          <input
            ref={inputRef}
            className="gsearch-input"
            value={query}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={mode === 'imei' ? t('globalSearch.placeholderImei') : t('globalSearch.placeholderPhone')}
            inputMode={mode === 'phone' ? 'tel' : 'text'}
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-expanded={showList}
            aria-controls="gsearch-list"
            aria-autocomplete="list"
            aria-label={t('globalSearch.label')}
            aria-invalid={status === 'invalid'}
            data-testid="gsearch-input"
          />
          {!query && <kbd className="gsearch-kbd" aria-hidden="true">Ctrl K</kbd>}
          <div className="gsearch-modes" role="group" aria-label={t('globalSearch.label')}>
            <button type="button" className={`gsearch-mode${mode === 'imei' ? ' active' : ''}`} onClick={() => switchMode('imei')} aria-pressed={mode === 'imei'} data-testid="gsearch-mode-imei">
              {t('globalSearch.modeImei')}
            </button>
            <button type="button" className={`gsearch-mode${mode === 'phone' ? ' active' : ''}`} onClick={() => switchMode('phone')} aria-pressed={mode === 'phone'} data-testid="gsearch-mode-phone">
              {t('globalSearch.modePhone')}
            </button>
          </div>
        </div>
        <button type="button" className={`gsearch-clear${query ? ' visible' : ''}`} onClick={clear} aria-label={t('globalSearch.clear')} tabIndex={query ? 0 : -1} data-testid="gsearch-clear">
          <CloseIcon />
        </button>

        {showList && (
          <div className="gsearch-panel" data-testid="gsearch-panel">
            <div className="gsearch-panel-head">
              <span>{typing ? t('globalSearch.suggestions') : t('globalSearch.recent')}</span>
              {!typing && (
                <button type="button" className="gsearch-panel-link" onClick={forgetRecent} data-testid="gsearch-forget">
                  {t('globalSearch.clearRecent')}
                </button>
              )}
            </div>
            <ul id="gsearch-list" role="listbox" className="gsearch-list">
              {listed.map(renderHit)}
            </ul>
          </div>
        )}

        {status !== 'idle' && (
          <div className={`gsearch-hint gsearch-hint-${status}`} role={status === 'searching' ? 'status' : 'alert'} data-testid="gsearch-hint">
            {status === 'searching' ? t('globalSearch.searching') : message[status]}
          </div>
        )}
      </form>

      {/* Mounted only once a device is found: the dialog's hook fetches the
          hardware types on mount, and the bar sits on every page. */}
      {device && (
        <DeviceDetailModal device={device} isOpen onClose={() => setDevice(null)} onDeviceUpdated={setDevice} />
      )}
    </>
  );
};
